/**
 * 가중 랜덤 선택 (Phaser/도메인 비의존, 순수 함수).
 *
 * loot table · gacha · 드롭 분포 · 레어도 추첨 등 "weight에 비례해 1개 뽑기" 전반에 사용.
 * 난수 소스를 주입받아 테스트 결정론을 확보한다.
 */

/** 0 이상 1 미만 난수 소스. 주입으로 테스트 결정론 확보 (기본 `Math.random`). */
export type RandomSource = () => number;

/** 가중 추첨 항목 — 선택될 값 + 가중치. */
export interface WeightedEntry<T> {
  readonly value: T;
  readonly weight: number;
}

/**
 * `entries`에서 weight에 비례해 1개를 추첨한다 (단일 `rng` 호출, 순수).
 * - 음수/NaN weight는 0으로 클램프된다.
 * - 모든 weight의 합이 0 이하이거나 `entries`가 비면 `fallback`을 반환한다.
 * - 부동소수 누적 오차로 마지막 항목이 선택되지 못하는 경우를 마지막 인덱스 폴백으로 방어한다.
 *
 * @example
 * weightedRoll(Math.random, [{ value: "a", weight: 3 }, { value: "b", weight: 1 }], "a");
 * // 75% 확률로 "a", 25% 확률로 "b"
 */
export function weightedRoll<T>(
  rng: RandomSource,
  entries: ReadonlyArray<WeightedEntry<T>>,
  fallback: T,
): T {
  let total = 0;
  const weights = entries.map((e) => {
    const w = e.weight > 0 ? e.weight : 0;
    total += w;
    return w;
  });
  if (total <= 0) return fallback;

  let roll = rng() * total;
  for (let i = 0; i < entries.length; i += 1) {
    roll -= weights[i]!;
    if (roll < 0) return entries[i]!.value;
  }
  return entries[entries.length - 1]!.value;
}
